import { useRef, useState } from "react";
import { Share2, Download } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import TipJar from "./TipJar";
import { Button } from "./ui/button";

// One-tap sharing for a member's jar: native share / copy link + a downloadable PNG.
export default function ShareJar({ jar, name, url, goalTitle }) {
  const cardRef = useRef();
  const [busy, setBusy] = useState(false);

  const shareLink = async () => {
    const text = `Help ${name} on Haplap 🤍`;
    try {
      if (navigator.share) await navigator.share({ title: "Haplap", text, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied 💛"); }
    } catch (_) { /* user dismissed */ }
  };

  const saveImage = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#FFFBF5" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "haplap-jar.png";
      a.click();
      toast.success("Jar image saved 💛");
    } catch (_) {
      toast.error("Couldn't make the image — please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-2">
        <Button data-testid="share-jar-link" onClick={shareLink} variant="outline" size="sm"
          className="rounded-full border-copper/40 text-copper hover:bg-copper/10">
          <Share2 className="w-4 h-4 mr-1.5" /> Share
        </Button>
        <Button data-testid="share-jar-image" onClick={saveImage} disabled={busy} variant="outline" size="sm" className="rounded-full">
          <Download className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save image"}
        </Button>
      </div>

      {/* off-screen share card captured for the downloadable image */}
      <div style={{ position: "fixed", left: -9999, top: 0 }} aria-hidden="true">
        <div ref={cardRef} style={{ width: 420 }} className="p-8 flex flex-col items-center text-center" data-testid="share-jar-card">
          <div className="flex items-center gap-2 mb-3">
            <span style={{ width: 16, height: 16, borderRadius: 999, background: "#CE8A2B", display: "inline-block" }} />
            <span style={{ color: "#7A4B3A", fontFamily: "'Fraunces', Georgia, serif", fontWeight: 800, fontSize: 26 }}>HAPLAP</span>
          </div>
          <TipJar balance={jar?.mode === "goal" ? jar.raised : jar?.total || 0} pct={jar?.pct} className="w-40 h-auto" />
          <p style={{ color: "#7A4B3A", fontFamily: "'Fraunces', Georgia, serif", fontWeight: 800, fontSize: 22, marginTop: 10 }}>{name}</p>
          {jar?.mode === "goal" ? (
            <p style={{ color: "#8a6f63", fontSize: 14, marginTop: 4 }}>
              {goalTitle || "Working toward a goal"} — ${Math.round(jar.raised || 0)} / ${Math.round(jar.goal || 0)}
            </p>
          ) : (
            <p style={{ color: "#8a6f63", fontSize: 14, marginTop: 4 }}>Help fill the jar 🤍</p>
          )}
          <p style={{ color: "#b08968", fontSize: 12, marginTop: 12, wordBreak: "break-all" }}>{url}</p>
        </div>
      </div>
    </div>
  );
}
